import React from 'react';
import { motion } from 'framer-motion';
import { QRCodeSVG } from 'qrcode.react';
import { FaApple, FaDownload, FaStar } from 'react-icons/fa';
import './Download.css';

const Download = () => {
  // Official App Store URL for DailySkin
  const appStoreUrl = "https://apps.apple.com/us/app/dailyskin-routine-reminder/id6752873618";

  const handleDownloadClick = () => {
    window.open(appStoreUrl, '_blank');
  };

  return (
    <section id="download" className="download section">
      <div className="container">
        <motion.div
          className="download-content"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="download-text">
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <h2 className="download-title">
                Start Your Glow Journey Today
              </h2>
              <p className="download-subtitle">
                Transform your skincare routine into an engaging adventure. Download DailySkin now and level up your self-care game.
              </p>
            </motion.div>

            <motion.div
              className="download-stats"
              initial={{ y: 30, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <div className="stat-item">
                <div className="stat-icon">
                  <FaStar />
                </div>
                <div className="stat-info">
                  <div className="stat-number">4.8</div>
                  <div className="stat-label">App Store Rating</div>
                </div>
              </div>
              <div className="stat-item">
                <div className="stat-icon">
                  <FaDownload />
                </div>
                <div className="stat-info">
                  <div className="stat-number">25K+</div>
                  <div className="stat-label">Glowing Users</div>
                </div>
              </div>
            </motion.div>

            <motion.div
              className="download-buttons"
              initial={{ y: 30, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <motion.button
                className="btn btn-download"
                onClick={handleDownloadClick}
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
              >
                <FaApple />
                Download for iOS
              </motion.button>
              
              <div className="download-info">
                <p>Free download • Requires iOS 14.0 or later</p>
                <p>Compatible with iPhone, iPad, and iPod touch</p>
              </div>
            </motion.div>
          </div>

          <motion.div
            className="qr-section"
            initial={{ x: 50, opacity: 0 }}
            whileInView={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            viewport={{ once: true }}
          >
            <div className="qr-container">
              <motion.div
                className="qr-code"
                whileHover={{ scale: 1.05 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <QRCodeSVG
                  value={appStoreUrl}
                  size={200}
                  bgColor="#ffffff"
                  fgColor="#000000"
                  level="M"
                  includeMargin={true}
                />
              </motion.div>
              <div className="qr-info">
                <h3>Scan to Download</h3>
                <p>Open your iPhone camera and point at the QR code to download DailySkin instantly</p>
              </div>
            </div>

            <motion.div
              className="download-features"
              initial={{ y: 30, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              viewport={{ once: true }}
            >
              <h4>What you get:</h4>
              <ul className="feature-list">
                <li>🏆 Level up system with XP points</li>
                <li>🔥 Daily streak tracking</li>
                <li>🔔 Smart skincare reminders</li>
                <li>📊 Progress analytics & insights</li>
                <li>🌟 Achievement badges to unlock</li>
                <li>💖 Personalized routine builder</li>
              </ul>
            </motion.div>
          </motion.div>
        </motion.div>

        <motion.div
          className="testimonials"
          initial={{ y: 50, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
        >
          <h3 className="testimonials-title">Loved by Skincare Enthusiasts Worldwide</h3>
          <div className="testimonials-grid">
            <motion.div
              className="testimonial"
              whileHover={{ y: -5 }}
              transition={{ duration: 0.3 }}
            >
              <div className="testimonial-stars">
                {'★'.repeat(5)}
              </div>
              <p className="testimonial-text">
                "DailySkin turned my inconsistent skincare routine into a fun daily habit! I love earning points and unlocking achievements for taking care of my skin."
              </p>
              <div className="testimonial-author">
                <div className="author-avatar">S</div>
                <div className="author-info">
                  <div className="author-name">Sarah M.</div>
                  <div className="author-location">Skincare Enthusiast</div>
                </div>
              </div>
            </motion.div>

            <motion.div
              className="testimonial"
              whileHover={{ y: -5 }}
              transition={{ duration: 0.3 }}
            >
              <div className="testimonial-stars">
                {'★'.repeat(5)}
              </div>
              <p className="testimonial-text">
                "The streak feature keeps me motivated! I'm on a 45-day streak and my skin has never looked better. The gamification really works!"
              </p>
              <div className="testimonial-author">
                <div className="author-avatar">M</div>
                <div className="author-info">
                  <div className="author-name">Maya K.</div>
                  <div className="author-location">Beauty Blogger</div>
                </div>
              </div>
            </motion.div>

            <motion.div
              className="testimonial"
              whileHover={{ y: -5 }}
              transition={{ duration: 0.3 }}
            >
              <div className="testimonial-stars">
                {'★'.repeat(5)}
              </div>
              <p className="testimonial-text">
                "Finally reached Level 20! The achievement system makes skincare feel like a game. My morning and evening routines are now automatic."
              </p>
              <div className="testimonial-author">
                <div className="author-avatar">J</div>
                <div className="author-info">
                  <div className="author-name">Jordan R.</div>
                  <div className="author-location">Glow Master</div>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Download;
